import {
  fetchSubscriptionState,
  SOFTWARE_SUBSCRIPTION_STATES,
} from 'utilities/software';
import { SOFTWARE_BASE_PRODUCT_KEYS } from 'constants/software';
import {
  BASE_PRODUCT_KEYS,
  PRODUCT_ALTERNATIVE_CATEGORIES,
} from 'constants/products';

/**
 * this is a list of preselcted add ons for products
 * format is as follows:
 * {
 *  [product_key]: [addOnCategory1, addOnCategory2, ...]
 * }
 */
const PRE_SELECTED_ADD_ONS = {
  // Bosstab is no longer pre-selected on the Stand Mount: "Floor Mount" is its
  // own Stand model option (bundle of s3b-kiosk + Bosstab floor stand).
  's3b-kiosk': ['software_kiosk'],
  'bundle-square-stand-with-floor-mount': ['software_kiosk'],
  's3-mount': ['software_kiosk', 'bosstab_stand'],
};

// The resolved Plus/Premium plan has both location and Kiosk device pricing.
// Legacy plans (including rollout and non-US fallbacks) must keep legacy copy.
const isKioskLiteAddOn = (addOn) => {
  const buckets =
    addOn.plan?.pricing_model?.multi_bucket_pricing?.buckets || [];
  return (
    addOn.base_product_key === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK &&
    buckets.some((bucket) => bucket.unit_type === 'BILLABLE_LOCATION') &&
    buckets.some((bucket) => bucket.unit_type === 'FNB_KIOSK_DEVICE')
  );
};

const hasKioskMountConfiguration = (
  canonicalProduct,
  alternatives = [],
  canonicalAlternativeMetadata = {},
) =>
  canonicalProduct?.base_product_key === BASE_PRODUCT_KEYS.K1A &&
  alternatives.some(
    (configuration) =>
      configuration.product_key !== canonicalProduct.product_key &&
      canonicalAlternativeMetadata[configuration.product_key]?.category ===
        PRODUCT_ALTERNATIVE_CATEGORIES.KIOSK_MOUNT &&
      !canonicalAlternativeMetadata[configuration.product_key]?.hidden,
  );

const withCanonicalPresentation = (purchasableProduct, canonicalProduct) => {
  const {
    slug,
    name,
    subtitle,
    description,
    short_description: shortDescription,
    disclaimer,
    specifications,
    marketing_name: marketingName,
    contentful_link: contentfulLink,
    banner,
    compatible_software: compatibleSoftware,
    three_dimensional_model: threeDimensionalModel,
    images,
    cta_subnav: ctaSubnav,
    promotion_banner: promotionBanner,
    tracking_data: trackingData,
    add_ons: addOns,
    add_ons_category_count: addOnsCategoryCount,
  } = canonicalProduct;

  return {
    ...purchasableProduct,
    slug,
    name,
    subtitle,
    description,
    short_description: shortDescription,
    disclaimer,
    specifications,
    marketing_name: marketingName,
    contentful_link: contentfulLink,
    banner,
    compatible_software: compatibleSoftware,
    three_dimensional_model: threeDimensionalModel,
    images,
    cta_subnav: ctaSubnav,
    promotion_banner: promotionBanner,
    tracking_data: trackingData,
    add_ons: addOns,
    add_ons_category_count: addOnsCategoryCount,
  };
};

const getKioskMountConfigurations = (
  alternatives = [],
  canonicalProduct,
  canonicalAlternativeMetadata = {},
) => {
  const relatedConfigurations = alternatives
    .map((configuration, index) => ({ configuration, index }))
    .filter(({ configuration }) => {
      const metadata = canonicalAlternativeMetadata[configuration.product_key];

      return (
        configuration.product_key !== canonicalProduct.product_key &&
        metadata?.category === PRODUCT_ALTERNATIVE_CATEGORIES.KIOSK_MOUNT &&
        !metadata.hidden
      );
    })
    .sort((first, second) => {
      const firstMetadata =
        canonicalAlternativeMetadata[first.configuration.product_key];
      const secondMetadata =
        canonicalAlternativeMetadata[second.configuration.product_key];
      const firstRank =
        firstMetadata?.relation_position ?? Number.MAX_SAFE_INTEGER;
      const secondRank =
        secondMetadata?.relation_position ?? Number.MAX_SAFE_INTEGER;

      return firstRank - secondRank || first.index - second.index;
    })
    .map(({ configuration }) => configuration);

  return [...relatedConfigurations, canonicalProduct];
};

const getInitialKioskProduct = ({
  product,
  alternatives = [],
  alternativesMetadata = {},
}) => {
  const canonicalAlternativeMetadata =
    alternativesMetadata[product?.product_key] || {};

  if (
    !hasKioskMountConfiguration(
      product,
      alternatives,
      canonicalAlternativeMetadata,
    )
  ) {
    return product;
  }

  const defaultConfiguration = getKioskMountConfigurations(
    alternatives,
    product,
    canonicalAlternativeMetadata,
  ).find((configuration) => configuration.product_key !== product.product_key);

  return defaultConfiguration && !defaultConfiguration.shipping?.out_of_stock
    ? withCanonicalPresentation(defaultConfiguration, product)
    : product;
};

/**
 *
 * @param {*} addOns
 * @returns addOns sorted into hardware or software
 */
const sortProductAddOnSections = (addOns) => {
  // since addOns are sorted by metadata category and not by taxon category
  // we need to check if the category is a software related one as they will be prefixed with 'software_'
  const softwareAddOnSections = [];
  const hardwareAddOnSections = [];

  if (!addOns) {
    return { softwareAddOnSections, hardwareAddOnSections };
  }

  Object.entries(addOns).forEach(([category, addOns]) => {
    if (category.startsWith('software_')) {
      softwareAddOnSections.push({ category, addOns });
    } else {
      hardwareAddOnSections.push({ category, addOns });
    }
  });

  return { softwareAddOnSections, hardwareAddOnSections };
};

/**
 * check if the product has any software add ons
 * @param {*} addOns product addOns
 * @returns boolean
 */
const checkAddOnsForSoftware = (addOns) => {
  const softwareAddOns = sortProductAddOnSections(addOns).softwareAddOnSections;
  return softwareAddOns.length > 0;
};

/**
 * extract software add ons from product
 * @param {*} addOns current product addOns addOns
 * @returns addOns
 */
const extractSoftwareAddOns = (addOns) => {
  const { softwareAddOnSections } = sortProductAddOnSections(addOns);
  return softwareAddOnSections.reduce((acc, section) => {
    return [...acc, ...section.addOns];
  }, []);
};

/**
 * @param {*} addOns - list of addOns from an addOn category
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed] - whether the user has an active P&P subscription
 * @returns {boolean} true if any of the addOns have an active subscription
 */
const checkAddOnsForActiveSubscription = (
  addOns,
  { isPnpSubscribed = false } = {},
) => {
  // Kiosk P&P subscribers still pay per-device, so their existing
  // subscription should not suppress the pricing selector or banner.
  const isKioskAddOn = addOns.some(
    (addOn) => addOn.base_product_key === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK,
  );
  if (isKioskAddOn && isPnpSubscribed) return false;

  return addOns.some((addOn) => {
    const subscriptionState = fetchSubscriptionState(addOn);

    return (
      subscriptionState === SOFTWARE_SUBSCRIPTION_STATES.ACTIVE_SUBSCRIPTION ||
      subscriptionState ===
        SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_WITH_ACTIVE_SUPERSET
    );
  });
};

export {
  isKioskLiteAddOn,
  checkAddOnsForActiveSubscription,
  checkAddOnsForSoftware,
  extractSoftwareAddOns,
  sortProductAddOnSections,
  PRE_SELECTED_ADD_ONS,
  hasKioskMountConfiguration,
  withCanonicalPresentation,
  getKioskMountConfigurations,
  getInitialKioskProduct,
};
